import tw from 'twin.macro';
import { useAtomValue } from 'jotai';
import { BriefcaseIcon, BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline';
import { CheckCircleIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';

import { PresentationCard, Title } from '@restworld/ui-ds';
import { WorkerResearchInfo } from '@restworld/utility-types';

import {
  jobTypeAtom,
  restaurantTypesAtom,
  workerPreferencesAtom,
  workerRolesAtom,
  workerShiftsAtom,
} from '../../../../employer-job-listing.atoms';
import { TabCardWrapper } from '../candidate-profile-section';

export const WorkerPreferences = () => {
  const { t } = useTranslation();
  const workerPreference = useAtomValue(workerPreferencesAtom);
  const errorMessage = <Title content={t('missing_info')} variant="h6-large" textColor="gray-500" />;
  const sector = (workerPreference?.research_info as unknown as WorkerResearchInfo)?.domain_interest_var?.label;
  const location = useAtomValue(restaurantTypesAtom);
  const role = useAtomValue(workerRolesAtom);
  const shifts = useAtomValue(workerShiftsAtom);
  const jobTypes = useAtomValue(jobTypeAtom);

  return (
    <div tw="pb-4">
      <TabCardWrapper title={t('preferences')}>
        <div id="preferences" tw="flex flex-col items-start gap-4 pt-4">
          <div tw="grid lg:grid-cols-2 grid-cols-1 lg:gap-x-8 lg:gap-y-4 gap-4 w-full">
            <PresentationCard
              label={t('common:sector')}
              header={sector || errorMessage}
              icon={<BriefcaseIcon tw="w-6 h-6" />}
              contentStyling={tw`text-gray-500`}
            />
            <PresentationCard
              label={t('common:local')}
              header={location || errorMessage}
              icon={<BuildingOfficeIcon tw="w-6 h-6" />}
            />
            <PresentationCard label={t('common:role')} header={role || errorMessage} icon={<UserIcon tw="w-6 h-6" />} />
          </div>
          <div tw="flex flex-col gap-4 items-start w-full mt-6">
            <Title variant="h6-large" fontWeight="medium" textColor="black">
              {t('common:working_hours')}
            </Title>
            <div tw="grid lg:grid-cols-2 grid-cols-1 gap-4 w-full">
              {shifts.map((shift, index) => (
                <div key={index} tw="flex flex-row gap-2 items-center justify-start">
                  <CheckCircleIcon tw="w-6 h-6 min-w-[24px] text-primary-700" />
                  <Title variant="h6-large" fontWeight="normal" textColor="gray-600">
                    {shift}
                  </Title>
                </div>
              ))}
              {shifts.length === 0 && errorMessage}
            </div>
          </div>
          <div tw="flex flex-col gap-4 items-start w-full mt-6">
            <Title variant="h6-large" fontWeight="medium" textColor="black">
              {t('common:work_commitment')}
            </Title>
            <div tw="grid lg:grid-cols-2 grid-cols-1 gap-4 w-full">
              {jobTypes
                ? jobTypes.map((job, index) => (
                    <div key={index} tw="flex flex-row gap-2 items-center justify-start">
                      <CheckCircleIcon tw="w-6 h-6 min-w-[24px] text-primary-700" />
                      <Title variant="h6-large" fontWeight="normal" textColor="gray-600">
                        {job.job_type_var.label}
                      </Title>
                    </div>
                  ))
                : null}
              {!jobTypes?.length && errorMessage}
            </div>
          </div>
        </div>
      </TabCardWrapper>
    </div>
  );
};
